/**
 * CHANGELOG INSTRUCTIONS:
 * 
 * To add a new version:
 * 1. Copy the TEMPLATE below
 * 2. Paste it at the TOP of the changelog array (newest first)
 * 3. Fill in the details
 * 4. Remove any unused fields
 * 5. Changes should be in a funny way, like a joke or a pun or sarcasm.
 * 
 * TEMPLATE:
{
  version: "1.0.0",
  date: "YYYY-MM-DD",
  type: "major", // Choose: major, minor, patch, feature, beta
  description: "Brief description of this version",
  changes: [
    "Change 1",
    "Change 2",
    "Change 3"
  ]
},
 */

// Types of changes and their colors - Professional muted palette inspired by Linear/GitHub/Figma
export const changeTypes = {
  major: {
    color: "#DC2626", // Muted red - professional and accessible
    label: "Major Release"
  },
  minor: {
    color: "#2563EB", // Professional blue - trustworthy and calm
    label: "New Features"
  },
  patch: {
    color: "#059669", // Muted green - success and fixes
    label: "Bug Fixes"
  },
  feature: {
    color: "#7C3AED", // Sophisticated purple - premium features
    label: "Enhancement"
  },
  beta: {
    color: "#EA580C", // Warm orange - experimental but stable
    label: "Beta Release"
  },
  update: {
    color: "#0891B2", // Professional teal - improvements and polish
    label: "Updates"
  }
};

// Add new versions at the TOP of this array (newest first)
export const changelog = [
  // Current Version
  {
    version: "2.2.6",
    date: "2026-02-03",
    type: "minor",
    description: "Weather at the hotel",
    changes: [
      "Show current weather on hotel details",
      "If the TV stops working maybe there is snow on the satellite dish"
    ]
  },
  {
    version: "2.2.5",
    date: "2026-01-14",
    type: "minor",
    description: "Archived Hotels Toggle - Because Sometimes You Need to Dig Up the Past (But Not All the Time)",
    changes: [
      "Added a shiny toggle to show archived hotels - now you can reunite with those old friends you thought were gone forever",
      "The toggle is off by default because let's be honest, archived hotels are like that box in your attic - you know it's there, but you don't need to see it every day",
    ]
  },
  {
    version: "2.2.4",
    date: "2025-01-17",
    type: "feature",
    description: "Collapsible Sidebar - Because Your Hotel Details Deserve More Space Than a Studio Apartment",
    changes: [
      "Made the hotel list sidebar collapsible by default - because apparently we all have trust issues with scrolling and prefer to see everything at once",
      "Added a sleek search bar in the updated welcome screen when the sidebar is collapsed, so you can find hotels without losing your current view (like a digital Swiss Army knife)"
    ]
  },
  {
    version: "2.2.3",
    date: "2025-07-23",
    type: "update",
    description: "Main Interface Redesign - Because Sometimes You Need to Throw Out the Old Furniture",
    changes: [
      "Completely redesigned the main interface with a cleaner, more responsive layout that actually scales properly on different screen sizes",
      "Rebuilt the UI architecture to be more scalable - no more components doing whatever they want like unruly house cats",
      "Improved overall visual hierarchy and spacing because apparently some of us have been living in design chaos this whole time"
    ]
  },
  {
    version: "2.2.2",
    date: "2025-07-01",
    type: "feature",
    description: "Strawberry Contacts - Your Personal Directory of Everyone Who Matters",
    changes: [
      "Added searchable contact directory to easily find anyone in the Strawberry organization - names, emails, and phone numbers all in one place",
      "Built with Google Contacts integration so your directory stays up-to-update automatically"
    ]
  },
  {
    version: "2.2.1",
    date: "2025-07-01",
    type: "feature",
    description: "Enhanced Handbook Library - Google Drive Integration That Actually Works (Most of the Time)",
    changes: [
      "Supercharged Google Drive integration with caching so good it remembers your documents faster than you remember where you put your keys",
      "Added 'Recently Viewed' section because apparently we all have the attention span of goldfish and need reminders of what we just read",
      "You can now edit documents in fullscreen mode - because sometimes you need to fix typos while pretending to be productive in a meeting"
    ]
  },
  {
    version: "2.2.0",
    date: "2025-06-23",
    type: "update",
    description: "UI Refinement, Search Bar Redesign & Data Import System - Because Pretty Matters (And So Does Functionality)",
    changes: [
      "Gave the UI a makeover - ditched those gradients like they were last season's cargo shorts and made everything pill-shaped because apparently we're living in the Age of Rounded Corners™",
      "New way to import data for easy data management - because copy-pasting is for peasants"
    ]
  },
  {
    version: "2.1.9",
    date: "2025-06-03",
    type: "feature",
    description: "Theme Engine & Compact View - Consistency is Key (Finally!)",
    changes: [
      "Built a centralized theme engine for consistent styling across the entire app - no more rouge components doing their own thing like rebellious teenagers",
      "Added compact view toggle for space-conscious users who want to fit more information than a game of Tetris",
      "Implemented smart two-column layout with automatic balancing - because nobody likes uneven columns, just like nobody likes uneven eyebrows",
      "Updated category colors to pride flag colors 🏳️‍🌈 - celebrating diversity one hotel card at a time",
    ]
  },
  {
    version: "2.1.8",
    date: "2025-05-12",
    type: "patch",
    description: "Hotel Navigation & Edit Mode Safety",
    changes: [
      "Fixed a bug that let you hotel-hop while editing (like trying to date multiple people at once - not cool)",
      "Added a friendly reminder that ghosting a hotel with unsaved changes is bad manners",
    ]
  },
  {
    version: "2.1.7",
    date: "2025-04-16",
    type: "feature",
    description: "Documentation Page for SOS",
    changes: [
      "Introduced a comprehensive documentation system for better knowledge sharing",
    ]
  },
  {
    version: "2.1.6",
    date: "2025-04-15", 
    type: "feature",
    description: "Feature Request System & SOS Scripts Page",
    changes: [
      "Added a Feature Request system where you can submit ideas and watch them sit in 'Under Consideration' until the heat death of the universe",
      "Built a Scripts page for the SOS team that's currently about as functional as a chocolate teapot (we're working on it)"
    ]
  },
  {
    version: "2.1.5",
    date: "2025-04-13", 
    type: "patch",
    description: "Shhh it's a secret",
    changes: [
      "Patch for unoptimized database reads."
    ]
  },
  {
    version: "2.1.4",
    date: "2025-04-10", 
    type: "feature",
    description: " You can now tag notes with a specific category ",
    changes: [
      "Added a way to tag notes with specific hotel categories to easily find what it relates to so you dont have to guess."
    ]
  },
  {
    version: "2.1.3",
    date: "2025-04-08", 
    type: "patch",
    description: "Fixed note display inconsistency between normal and legacy notes",
    changes: [
      "Fixed a bug that was playing hide and seek with your notes - turns out normal notes and legacy notes weren't getting along like divorced parents at a wedding. They're now forced to share the same space peacefully."
    ]
  },
  {
    version: "2.1.2",
    date: "2025-04-07", 
    type: "feature",
    description: "Added data export functionality, enhanced search capabilities, and UI improvements to Weekly Statistics",
    changes: [
      "Added data export because copying and pasting is so last century. Now you can filter and export exactly what you want, like a data-hoarder.",
      "Added a search filter so powerful it can find your lost socks, your car keys, and maybe even your will to live - as long as they're in the database!",
      "Made the celebration image in Weekly Statistics smaller because apparently not everyone wants a giant image taking up their entire screen, and added a dismiss button for those who hate joy."
    ]
  },
  {
    version: "2.1.1",
    date: "2025-04-01", 
    type: "update",
    description: "Added legacy note support and hotel status filtering",
    changes: [
      "Gave those dusty old legacy notes a makeover that even Marie Kondo would approve of!",
      "Finally added a filter for Active/WIP hotels because scrolling is so 2024.",
    ]
  },
  {
    version: "2.1.0",
    date: "2025-03-31",
    type: "feature",
    description: "Enhanced user experience with new navigation and changelog features",
    changes: [
      "Added a changelog because Andreas threatened to quit using the app if we didn't, and we're not ready to lose our most vocal user (even if he's always asking for features).",
      "Added Hotel View option because someone asked 'where's the hotels?' one too many times.",
    ]
  },
  {
    version: "2.0.9",
    date: "2025-03-30",
    type: "update",
    description: "Enhanced UI/UX with modern design updates and improved navigation",
    changes: [
      "Darkmode got a glow-up and now it's darker than my soul.",
      "Rolled out a fancy new navigation bar that's smoother than butter.",

    ]
  },

];

/**
 * VERSION NAMING CONVENTION:
 * 
 * MAJOR.MINOR.PATCH
 * Example: 1.2.3
 * 
 * - MAJOR: Breaking changes or significant new features
 * - MINOR: New features with backward compatibility
 * - PATCH: Bug fixes and small improvements
 * 
 * For beta versions, use: 0.MAJOR.MINOR
 * Example: 0.9.0
 */
